import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Globe, Plug } from "lucide-react";
import { Card, Field, Label, SettingsForm, PRIMARY, useSettings } from "./shared";
import { CmsLogo, StatusBadge, cmsById, cmsStatus } from "./cms/catalog";

export default function SiteSection() {
  const { form, setForm, handleChange, schedule, loading, cmsConn } = useSettings();
  const isWebflow = cmsConn?.cms_type === "webflow";
  const activeCms = cmsById(cmsConn?.cms_type);
  const onChange = handleChange;

  return (
    <SettingsForm>
      <Card icon={Plug} title="Content Source" subtitle="Where articles are synced from">
        {activeCms ? (
          <div className="flex items-center gap-3">
            <CmsLogo cms={activeCms} size={36} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-bold text-zinc-900">{activeCms.name}</p>
                {!loading && <StatusBadge status={cmsStatus(activeCms, cmsConn)} />}
              </div>
              <p className="text-[10px] text-zinc-400">{cmsConn?.connected_as || activeCms.via}</p>
            </div>
            <Link to={`../cms/${activeCms.id}`} relative="path" data-testid="manage-cms-link"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-900 underline underline-offset-4 decoration-zinc-300">
              Manage <ArrowUpRight size={11} />
            </Link>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">No CMS connected yet.</p>
        )}
        <Link to="../cms" relative="path" className="text-[10px] text-zinc-500 hover:text-zinc-900">
          {activeCms ? "See all supported CMS →" : "Connect a CMS →"}
        </Link>
      </Card>

      <Card icon={Globe} title="Live Site URLs" subtitle="How article slugs map to public URLs">
        <Field label="Live Site URL" required type="url" name="astro_site_base_url" value={form.astro_site_base_url} onChange={onChange}
          placeholder="https://yoursite.com" data-testid="astro-site-base-url-input"
          hint={'Your website (same as Business Context → Website URL). Also builds "view live" links, GSC page matching and Bulk Indexing URLs from each article\'s slug.'} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="URL Path Prefix" optional mono name="astro_url_prefix" value={form.astro_url_prefix} onChange={onChange}
            placeholder="blog" data-testid="astro-url-prefix-input"
            hint={isWebflow
              ? 'Blank uses the collection\'s own slug (Webflow serves items at /{collection-slug}/{item-slug}).'
              : 'Route segment the collection is served under (e.g. "blog" for /blog/slug). Blank if posts live at the site root.'} />
          {!isWebflow && <Field label="Default Locale" optional mono name="astro_default_locale" value={form.astro_default_locale} onChange={onChange}
            placeholder="en" data-testid="astro-default-locale-input"
            hint={'Locale folder that\'s unprefixed on the live site (e.g. "en" for /blog/slug vs /id/blog/slug). Blank if every locale is prefixed.'} />}
        </div>
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          Changing these only affects existing articles after a <span className="font-bold">Full Sync</span> on Overview — a regular sync skips unchanged content.
        </p>
      </Card>

      <Card icon={Clock} title="Auto-Sync" subtitle="Scheduled Sync">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-zinc-900">Enable Auto-Sync</p>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox" name="auto_sync_enabled"
              checked={form.auto_sync_enabled} onChange={handleChange}
              data-testid="auto-sync-toggle"
              className="sr-only peer"
            />
            <div className="w-9 h-4 bg-zinc-200 peer-checked:bg-zinc-900 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:w-3.5 after:h-3.5 after:transition-all peer-checked:after:translate-x-5 rounded-full" />
          </label>
        </div>

        {form.auto_sync_enabled && (
          <div>
            <Label className="mb-1.5">Interval</Label>
            <div className="flex gap-1.5 flex-wrap">
              {[6, 12, 24, 48].map(h => (
                <button
                  key={h} type="button"
                  onClick={() => setForm(f => ({ ...f, sync_interval_hours: h }))}
                  data-testid={`interval-${h}h`}
                  className={`px-3 py-1.5 text-[10px] font-bold transition-colors rounded-lg ${
                    Number(form.sync_interval_hours) === h
                      ? "text-white"
                      : "bg-white border border-zinc-100 text-zinc-500 hover:bg-zinc-50"
                  }`}
                  style={{ backgroundColor: Number(form.sync_interval_hours) === h ? PRIMARY : "" }}
                >
                  {h}h
                </button>
              ))}
            </div>
          </div>
        )}

        {schedule?.next_run && (
          <div className="bg-zinc-50 border border-zinc-100 px-3 py-2.5 flex items-center gap-2 rounded-lg">
            <Clock size={11} strokeWidth={2} className="text-green-600" />
            <p className="text-[10px] text-zinc-500">
              Next: <span className="font-bold text-green-700">{new Date(schedule.next_run).toLocaleString()}</span>
            </p>
          </div>
        )}
      </Card>
    </SettingsForm>
  );
}
